import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../../firebase/config'; // ◄ SOLUCIÓN: Agregamos 'auth' en la importación
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { saveToFirestore } from '../../firebase/cleanPayload';

import { 
    parseCurrency, 
    formatCurrencyForInput, 
    formatDateForInput,
    mapMonedaToDB,
    mapMonedaToVisual
} from '../../utils/segurosUtils';

// Importamos las listas centralizadas
import { refacturacionEstados, formaDespacho } from '../../data/listasDesplegables';

const TIPOS_ENDOSO = ['Refacturación', 'Aumento', 'Reducción', 'Anulación'];

/**
 * ModalRefacturacion - Gestión de renovaciones, aumentos, reducciones y anulaciones de pólizas.
 * Refactorizado bajo arquitectura limpia con consumo de caché global y estilos puros de Tailwind.
 */
const ModalRefacturacion = ({ show, onClose, onSuccess, poliza, clientData, initialData = null }) => {
    // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 2. ESTADOS LOCALES ---
    const [formData, setFormData] = useState({ 
        fechaCarga: '',
        pagoEfectuado: false,
        motivoAnulacion: '',
        fechaAnulacion: '',
        quienGestiono: '',
        fechaReclamo: '',
        observacionesAnulacion: '',
        numeroFactura: '',
        estadoRefacturacion: '',
        tipoEndoso: 'Refacturación'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const esAnulacion = formData.tipoEndoso === 'Anulación';
    const esNegativo = ['Anulación', 'Reducción'].includes(formData.tipoEndoso);
    
    // Filtrado de estados para el selector principal (excluyendo los de pago)
    const estadosFiltrados = useMemo(() => {
        return refacturacionEstados.filter(e => !['ABONADO', 'DEBITADA'].includes(e.toUpperCase()));
    }, []);

    // Estados específicos para la sección de cobro
    const estadosSoloPago = useMemo(() => {
        return refacturacionEstados.filter(e => ['ABONADO', 'DEBITADA'].includes(e.toUpperCase()));
    }, []);

    // --- 3. HIDRATACIÓN DINÁMICA DE LA REFACTURACIÓN ---
    useEffect(() => {
        if (show && poliza) {
            if (initialData) {
                const estadoCargado = initialData.estadoRefacturacion || initialData.estadoGestionRenovacion || '';
                const isPaid = ['ABONADO', 'DEBITADA'].includes(estadoCargado.toUpperCase());
                
                setFormData({
                    ...initialData,
                    fechaCarga: formatDateForInput(initialData.fechaCarga),
                    pagoEfectuado: isPaid,
                    tipoEndoso: initialData.tipoEndoso || 'Refacturación',
                    prima: formatCurrencyForInput(initialData.prima),
                    primaEmitida: formatCurrencyForInput(initialData.primaEmitida),
                    inicioVigencia: formatDateForInput(initialData.inicioVigencia),
                    finVigencia: formatDateForInput(initialData.finVigencia),
                    fechaCobro: formatDateForInput(initialData.fechaCobro),
                    fechaDespacho: formatDateForInput(initialData.fechaDespacho),
                    fechaReclamo: formatDateForInput(initialData.fechaReclamo),
                    motivoAnulacion: initialData.motivoAnulacion || '',
                    fechaAnulacion: formatDateForInput(initialData.fechaAnulacion),
                    quienGestiono: initialData.quienGestiono || '',
                    observacionesAnulacion: initialData.observacionesAnulacion || '',
                    numeroFactura: initialData.numeroFactura || '',
                    estadoRefacturacion: estadoCargado,
                    moneda: mapMonedaToVisual(initialData.moneda || poliza.moneda || '$')
                });
            } else {
                setFormData({
                    fechaCarga: new Date().toISOString().split('T')[0],
                    tipoEndoso: 'Refacturación',
                    estadoRefacturacion: '',
                    inicioVigencia: '', 
                    finVigencia: '', 
                    numeroEndoso: '', 
                    numeroFactura: '',
                    moneda: mapMonedaToVisual(poliza.moneda || '$'),
                    prima: '', 
                    primaEmitida: '', 
                    quienCobro: '', 
                    fechaCobro: '',
                    fechaDespacho: '', 
                    formaDespacho: '',
                    tipoGestion: 'Refacturacion', 
                    pagoEfectuado: false,
                    motivoAnulacion: '',
                    fechaAnulacion: new Date().toISOString().split('T')[0],
                    quienGestiono: '',
                    fechaReclamo: '',
                    observacionesAnulacion: ''
                });
            }
        }
    }, [show, poliza, initialData]);

    // --- 4. MEMORIZACIÓN Y ORDENAMIENTO EN CACHÉ LOCAL (useMemo) ---
    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const sortedDespachos = useMemo(() => {
        return [...(formaDespacho || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    const sortedEstadosRefacturacion = useMemo(() => {
        const premezcla = [...(formData.pagoEfectuado ? ["Abonado", "Debitada"] : []), ...estadosFiltrados];
        // Eliminamos duplicados por si acaso y ordenamos
        return [...new Set(premezcla)].sort((a, b) => a.localeCompare(b));
    }, [formData.pagoEfectuado, estadosFiltrados]);

    // --- 5. ACCIONES Y HANDLERS DE ENTRADA ---
    const handlePrimaChange = (e, field) => {
        let value = e.target.value.replace(/\D/g, ''); 
        if (value) {
            const number = Number(value) / 100;
            setFormData(prev => ({ ...prev, [field]: formatCurrencyForInput(number) }));
        } else {
            setFormData(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'numeroEndoso' || name === 'numeroFactura') {
            setFormData(prev => ({ ...prev, [name]: value.replace(/\D/g, '') }));
            return;
        }
        
        if (name === 'tipoEndoso') {
            setFormData(prev => ({ 
                ...prev, 
                tipoEndoso: value,
                estadoRefacturacion: value === 'Anulación' ? 'ANULADA' : (prev.estadoRefacturacion === 'ANULADA' ? 'PENDIENTE' : prev.estadoRefacturacion)
            }));
            return;
        }

        if (name === 'pagoEfectuado') {
            setFormData(prev => ({ 
                ...prev, 
                pagoEfectuado: checked,
                estadoRefacturacion: checked ? 'Abonado' : (['ABONADO', 'DEBITADA'].includes(prev.estadoRefacturacion.toUpperCase()) ? 'PENDIENTE' : prev.estadoRefacturacion)
            }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // --- 6. PROCESAMIENTO Y GUARDADO FINAL ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!poliza || isSubmitting) return;
        setIsSubmitting(true);

        try {
            const factor = esNegativo ? -1 : 1;
            const valPrima = Math.abs(parseCurrency(formData.prima)) * factor;
            const valPrimaEmitida = Math.abs(parseCurrency(formData.primaEmitida)) * factor;

            const payload = { 
                ...formData,
                moneda: mapMonedaToDB(formData.moneda),
                prima: valPrima,
                primaEmitida: valPrimaEmitida,
                ultimaModificacion: serverTimestamp(),
                clienteId: clientData?.id || poliza.clienteId || 'unknown',
                clienteNombre: clientData?.nombre || poliza.clienteNombre || '',
                clienteApellido: clientData?.apellido || poliza.clienteApellido || '',
                clienteCuit: clientData?.cuit || poliza.clienteCuit || '',
                clienteDni: clientData?.dni || poliza.clienteDni || '',
                polizaId: poliza.id,
                nPoliza: poliza.nPoliza,
                compania: poliza.compania,
                companiaId: poliza.companiaId,
                riesgo: poliza.riesgo,
                riesgoId: poliza.riesgoId,
                subriesgo: poliza.subriesgo || '',
                canal: poliza.canal || '',
                canalNombre: poliza.canalNombre || '',
                canalNombreId: poliza.canalNombreId || '',
                subcanalNombre: poliza.subcanalNombre || '',
                subcanalNombreId: poliza.subcanalNombreId || '',
                formaPago: poliza.formaPago || '',
            };

            delete payload.pagoEfectuado; 
            delete payload.estadoGestionRenovacion;

            if (!esAnulacion) {
                delete payload.fechaAnulacion;
                delete payload.motivoAnulacion;
                delete payload.observacionesAnulacion;
            }

            const clientId = clientData?.id || poliza.clienteId;
            const polizaId = poliza.id;
            const endososPath = `public/data/clientes/${clientId}/polizas/${polizaId}/endosos`;
            const polizaRef = doc(db, `public/data/clientes/${clientId}/polizas`, polizaId);

            await saveToFirestore(endososPath, payload, initialData?.id);

            // Sincronización en cascada sobre la póliza matriz si es el endoso inicial ("0")
            if (String(formData.numeroEndoso) === '0') {
                await updateDoc(polizaRef, {
                    ref_estadoGestion: formData.estadoRefacturacion,
                    ref_fechaCobro: formData.fechaCobro || '',
                    ref_fechaDespacho: formData.fechaDespacho || '',
                    ref_finVigencia: formData.finVigencia,
                    ref_formaDespacho: formData.formaDespacho || '',
                    ref_inicioVigencia: formData.inicioVigencia,
                    ref_pagoEfectuado: formData.pagoEfectuado,
                    ref_prima: valPrima,
                    ref_primaEmitida: valPrimaEmitida,
                    ref_quienCobro: formData.quienCobro || '',
                    ultimaModificacion: serverTimestamp()
                });
            }

            // Sincronización de anulación completa en la póliza matriz
            if (payload.tipoEndoso === 'Anulación') {
                await updateDoc(polizaRef, { 
                    estadoEmision: 'ANULADA',
                    motivoAnulacion: formData.motivoAnulacion,
                    fechaAnulacion: formData.fechaAnulacion, 
                    quienGestiono: formData.quienGestiono,
                    observacionesAnulacion: formData.observacionesAnulacion,
                    primaAnulacion: valPrima,
                    ultimaModificacion: serverTimestamp()
                });
            }

            onSuccess(initialData ? 'Movimiento actualizado correctamente.' : 'Movimiento cargado correctamente.', 'success');
            onClose(); 
        } catch (error) { 
            console.error("Error crítico al guardar movimiento de refacturación:", error); 
            onSuccess(`Error al guardar: ${error.message}`, 'error'); 
        } finally { 
            setIsSubmitting(false); 
        }
    };

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1010] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-slate-100 animate-fade-in text-left font-sans transition-all duration-300">
                
                {/* CABECERA DEL MODAL */}
                <div className="flex justify-between items-center px-8 py-4 border-b border-slate-100 shrink-0 bg-white select-none">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800 leading-none">{initialData ? 'Editar Refacturación' : 'Cargar Refacturación'}</h3>
                        <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mt-1.5">Póliza #{poliza.nPoliza} • {poliza.compania}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-50 rounded-full">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL MODAL (Scroll optimizado) */}
                <div className="overflow-y-auto p-8 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    <form id="refacturacionForm" onSubmit={handleSubmit} className="space-y-8">
                        
                        {/* SECCIÓN I: DETALLES DEL MOVIMIENTO */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">Detalles del Movimiento</legend>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1 select-none">Fecha de Carga</label>
                                    <input type="date" name="fechaCarga" value={formData.fechaCarga || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-400 outline-none bg-slate-50/50 h-[46px] select-none cursor-not-allowed" readOnly />
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tipo de Endoso *</label>
                                    <select name="tipoEndoso" value={formData.tipoEndoso || 'Refacturación'} onChange={handleChange} className="w-full border border-blue-200 rounded-xl px-4 py-2.5 text-sm font-bold text-blue-700 outline-none bg-blue-50/10 h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required>
                                        {TIPOS_ENDOSO.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° de Endoso</label>
                                    <input type="text" name="numeroEndoso" value={formData.numeroEndoso || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" placeholder="Solo números" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 mt-2 border-t border-slate-100">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° de Factura</label>
                                    <input type="text" name="numeroFactura" value={formData.numeroFactura || ''} onChange={handleChange} className="w-full border border-orange-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-orange-700 outline-none bg-white h-[46px] focus:border-orange-400 focus:ring-1 focus:ring-orange-100 transition-all" placeholder="Solo números" />
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Estado Gestión *</label>
                                    <select 
                                        name="estadoRefacturacion" 
                                        value={formData.estadoRefacturacion || ''} 
                                        onChange={handleChange} 
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" 
                                        required 
                                        disabled={formData.pagoEfectuado}
                                    >
                                        <option value="">Seleccione...</option>
                                        {sortedEstadosRefacturacion.map(e => (
                                            <option key={e} value={e}>{e}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Inicio de Vigencia *</label>
                                    <input type="date" name="inicioVigencia" value={formData.inicioVigencia || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 mt-2 border-t border-slate-100">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fin de Vigencia *</label>
                                    <input type="date" name="finVigencia" value={formData.finVigencia || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" required />
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-indigo-700 mb-1.5 pl-1">Responsable de Gestión</label>
                                    <select name="quienGestiono" value={formData.quienGestiono || ''} onChange={handleChange} className="w-full border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-medium text-indigo-900 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all">
                                        <option value="">Seleccione Responsable...</option>
                                        {sortedColaboradores.map(v => (
                                            <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-indigo-700 mb-1.5 pl-1">Fecha Último Reclamo</label>
                                    <input type="date" name="fechaReclamo" value={formData.fechaReclamo || ''} onChange={handleChange} className="w-full border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-medium text-indigo-900 outline-none bg-white h-[46px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all" />
                                </div>
                            </div>
                        </fieldset>

                        {/* SECCIÓN CONDICIONAL: DETALLES DE ANULACIÓN */}
                        {esAnulacion && (
                            <fieldset className="border border-red-200 rounded-xl p-6 relative bg-red-50/10 animate-fade-in text-left">
                                <legend className="px-3 text-base font-bold text-red-700 bg-white absolute -top-3.5 left-4 flex items-center gap-2 tracking-tight select-none">
                                     <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                     </svg>
                                     Datos Específicos de Anulación
                                </legend>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                    <div>
                                        <label className="block text-[11px] font-bold text-red-800 uppercase tracking-wide mb-1.5 pl-1">Motivo Anulación *</label>
                                        <select name="motivoAnulacion" value={formData.motivoAnulacion} onChange={handleChange} className="w-full border border-red-200 rounded-xl px-4 py-2.5 text-sm font-bold text-red-900 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required={esAnulacion}>
                                            <option value="">Seleccione motivo...</option>
                                            <option value="DESESTIMIENTO">DESESTIMIENTO</option>
                                            <option value="INCOBRABLE">INCOBRABLE</option>
                                            <option value="SINIESTRO">SINIESTRO</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-bold text-red-800 uppercase tracking-wide mb-1.5 pl-1">Fecha Anulación *</label>
                                        <input type="date" name="fechaAnulacion" value={formData.fechaAnulacion} onChange={handleChange} className="w-full border border-red-200 rounded-xl px-4 py-2.5 text-sm font-bold text-red-900 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required={esAnulacion} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-bold text-red-800 uppercase tracking-wide mb-1.5 pl-1">¿Quién Gestionó la baja? *</label>
                                        <select name="quienGestiono" value={formData.quienGestiono} onChange={handleChange} className="w-full border border-red-200 rounded-xl px-4 py-2.5 text-sm font-bold text-red-900 outline-none bg-white h-[46px] focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all" required={esAnulacion}>
                                            <option value="">Seleccione...</option>
                                            {sortedColaboradores.map(v => (
                                                <option key={v.id} value={v.nombre}>{v.nombre}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-[11px] font-bold text-red-800 uppercase tracking-wide mb-1.5 pl-1">Observaciones de la Anulación</label>
                                        <textarea 
                                            name="observacionesAnulacion" 
                                            value={formData.observacionesAnulacion} 
                                            onChange={handleChange} 
                                            rows="2" 
                                            className="w-full border border-red-200 rounded-xl px-4 py-3 text-sm font-medium text-red-900 outline-none bg-white min-h-[80px] resize-none focus:border-red-500 focus:ring-1 focus:ring-red-100 transition-all placeholder:text-red-300" 
                                            placeholder="Detalle los motivos, reclamos o particularidades de la baja del contrato..."
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* SECCIÓN II: VALORES */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">Valores</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Prima de la Caución</label>
                                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all shadow-sm">
                                        <div className="bg-slate-50 border-r border-slate-100 px-3 h-full flex items-center shrink-0 select-none">
                                            <select name="moneda" value={formData.moneda || 'ARS $'} onChange={handleChange} className="bg-transparent font-bold text-slate-500 outline-none cursor-pointer uppercase text-xs">
                                                <option value="ARS $">ARS $</option>
                                                <option value="USD U$S">USD u$s</option>
                                            </select>
                                        </div>
                                        <input type="text" name="prima" value={formData.prima || ''} onChange={(e) => handlePrimaChange(e, 'prima')} className="w-full px-4 outline-none font-mono font-bold text-slate-800 text-lg h-full" placeholder="0,00" />
                                    </div>
                                    {esNegativo && <p className="text-[10px] text-red-500 mt-1.5 font-bold italic text-left pl-1 select-none">Se guardará como valor negativo (-)</p>}
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Importe de la Caución</label>
                                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100 transition-all shadow-sm">
                                        <div className="bg-slate-50 border-r border-slate-100 px-4 h-full flex items-center font-bold text-slate-500 text-sm shrink-0 select-none">
                                            {formData.moneda || poliza.moneda || '$'}
                                        </div>
                                        <input type="text" name="primaEmitida" value={formData.primaEmitida || ''} onChange={(e) => handlePrimaChange(e, 'primaEmitida')} className="w-full px-4 outline-none font-mono font-bold text-slate-800 text-lg h-full" placeholder="0,00" />
                                    </div>
                                    {esNegativo && <p className="text-[10px] text-red-500 mt-1.5 font-bold italic text-left pl-1 select-none">Se guardará como valor negativo (-)</p>}
                                </div>
                            </div>
                        </fieldset>

                        {/* SECCIÓN III: DESPACHO */}
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">Despacho</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Forma de Despacho</label>
                                    <select name="formaDespacho" value={formData.formaDespacho || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all">
                                        <option value="">Seleccione...</option>
                                        {sortedDespachos.map((fd, i) => (
                                            <option key={i} value={fd}>{fd}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha de Despacho</label>
                                    <input type="date" name="fechaDespacho" value={formData.fechaDespacho || ''} onChange={handleChange} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all" />
                                </div>
                            </div>
                        </fieldset>

                        {/* SECCIÓN IV: COBRO FINALIZADO */}
                        <fieldset className="border border-indigo-200 rounded-xl p-6 relative bg-indigo-50/10 shadow-sm">
                            <legend className="px-3 text-base font-bold text-indigo-700 bg-white absolute -top-3.5 left-4 tracking-tight select-none">Cobro Finalizado</legend>
                            
                            <div className="flex flex-col gap-6 pt-2 text-left select-none">
                                <div className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                                    formData.pagoEfectuado 
                                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg' 
                                        : 'bg-white border-slate-100 text-slate-400 hover:border-emerald-200'
                                }`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                            formData.pagoEfectuado ? 'bg-white text-emerald-600' : 'bg-slate-100 text-slate-400'
                                        }`}>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <p className={`text-sm font-black uppercase tracking-wider ${formData.pagoEfectuado ? 'text-white' : 'text-slate-500'}`}>Registrar Cobro / Pago Efectuado</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" name="pagoEfectuado" checked={formData.pagoEfectuado || false} onChange={handleChange} className="sr-only peer" />
                                        <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500 shadow-inner"></div>
                                    </label>
                                </div>

                                {formData.pagoEfectuado && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in px-2">
                                        <div>
                                            <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">Estado Gestión *</label>
                                            <select 
                                                name="estadoRefacturacion" 
                                                value={formData.estadoRefacturacion || ''} 
                                                onChange={handleChange} 
                                                className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all"
                                                required={formData.pagoEfectuado}
                                            >
                                                <option value="">Seleccione...</option>
                                                {estadosSoloPago.map(e => <option key={e} value={e}>{e}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">¿Quién Cobró? *</label>
                                            <select name="quienCobro" value={formData.quienCobro || ''} onChange={handleChange} className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" required={formData.pagoEfectuado}>
                                                <option value="">Seleccione...</option>
                                                {sortedColaboradores.map(p => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5 pl-1">Fecha de Cobro *</label>
                                            <input type="date" name="fechaCobro" value={formData.fechaCobro || ''} onChange={handleChange} className="w-full border border-emerald-200 rounded-xl px-4 py-2.5 text-sm font-bold text-emerald-950 outline-none bg-white h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 transition-all" required={formData.pagoEfectuado} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </fieldset>

                    </form>
                </div>

                {/* BOTONERA DE ACCIONES INFERIOR */}
                <div className="px-8 py-5 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 shrink-0 select-none">
                    <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold hover:bg-slate-50 shadow-sm text-xs uppercase tracking-wider transition-all transform active:scale-95">Cancelar</button>
                    <button type="submit" form="refacturacionForm" disabled={isSubmitting} className={`px-8 py-2.5 rounded-full text-white font-bold transition-all text-xs uppercase tracking-wider transform active:scale-95 shadow-lg min-w-[200px] ${
                        formData.pagoEfectuado 
                            ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100' 
                            : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                    }`}>
                        {isSubmitting ? 'Guardando...' : initialData ? 'Confirmar Cambios' : 'Guardar Refacturación'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default ModalRefacturacion;